import { useState, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { brand, surface, text, fontSize, radius, space, elevation } from '@/styles/tokens'
import { useRegisterConfirmStore } from '@/stores/registerConfirm'

/**
 * 首次注册前的邀请码确认弹窗。
 *
 * 由 useWalletAuth 在拿到 /auth/nonce 的 registered=false 之后、发起签名之前唤起：
 * 推荐关系一旦随注册写入就永久绑定，用户自己改不了，所以必须在签名前看一眼。
 *
 * 全局只渲染一份（挂在 App 根），状态走 useRegisterConfirmStore，
 * 这样 Header 和页面内哪一处触发登录都能弹出来。
 */
export default function InviteConfirmModal() {
  const { t } = useTranslation()
  const pending = useRegisterConfirmStore((s) => s.pending)
  const confirm = useRegisterConfirmStore((s) => s.confirm)
  const cancel = useRegisterConfirmStore((s) => s.cancel)

  const [code, setCode] = useState('')
  // 从邀请链接带进来的邀请码默认只读展示 —— 这一步是「确认」不是「填写」，
  // 直接给个可编辑输入框会让人以为要自己输，也更容易误改。
  // 没带邀请码时（开关关闭的顶号场景）才直接进编辑态。
  const [editing, setEditing] = useState(false)

  // 每次弹出时以链接里带来的邀请码为初值；用户可以改（链接拿错了能当场纠正）
  useEffect(() => {
    if (pending) {
      setCode(pending.inviteCode)
      setEditing(!pending.inviteCode)
    }
  }, [pending])

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') cancel()
    },
    [cancel],
  )

  useEffect(() => {
    if (!pending) return
    document.addEventListener('keydown', handleKeyDown)
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.body.style.overflow = ''
    }
  }, [pending, handleKeyDown])

  if (!pending) return null

  const trimmed = code.trim()
  // 没填邀请码、后台又要求必须有 → 不能继续，按钮置灰并说明原因
  const blocked = !trimmed && pending.inviteCodeRequired
  // 没填邀请码、后台也不要求 → 允许注册，但要明确告知会成为无推荐人的顶级账号
  const asRoot = !trimmed && !pending.inviteCodeRequired

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-end justify-center">
      <div
        className="fixed inset-0 animate-fade-in"
        style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)' }}
        onClick={cancel}
      />

      <div
        className="relative w-full overflow-hidden animate-slide-up"
        style={{
          maxWidth: 430,
          background: surface.raised,
          borderTopLeftRadius: radius.xl,
          borderTopRightRadius: radius.xl,
          borderTop: `1px solid ${brand.blue}40`,
          boxShadow: elevation.glow(brand.blue, 0.12),
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-center pt-3 pb-4">
          <div className="w-10 h-1 rounded-full" style={{ background: `${brand.blue}4D` }} />
        </div>

        <div style={{ padding: `0 ${space.xl}px ${space.xxl}px` }}>
          <div className="flex justify-center" style={{ marginBottom: space.lg }}>
            <div
              className="flex items-center justify-center"
              style={{
                width: 64,
                height: 64,
                borderRadius: radius.lg,
                background: `linear-gradient(135deg, ${brand.blue}33, ${brand.violet}1A)`,
                border: `1px solid ${brand.blue}4D`,
              }}
            >
              <span className="material-icons" style={{ color: brand.blue, fontSize: 32 }}>
                {asRoot ? 'account_tree' : 'group_add'}
              </span>
            </div>
          </div>

          <h3
            className="text-center font-semibold"
            style={{ color: text.primary, fontSize: fontSize.title, marginBottom: space.sm }}
          >
            {t('inviteConfirm.title')}
          </h3>
          <p
            className="text-center"
            style={{
              color: text.secondary,
              fontSize: fontSize.body,
              lineHeight: 1.6,
              marginBottom: space.xl,
            }}
          >
            {t('inviteConfirm.subtitle')}
          </p>

          <label
            className="block font-medium"
            style={{ color: text.secondary, fontSize: fontSize.caption, marginBottom: space.sm }}
          >
            {t('inviteConfirm.codeLabel')}
          </label>

          {editing ? (
            <input
              type="text"
              value={code}
              autoFocus
              onChange={(e) => setCode(e.target.value)}
              placeholder={t('inviteConfirm.codePlaceholder')}
              className="w-full outline-none"
              style={{
                height: 48,
                padding: `0 ${space.lg}px`,
                borderRadius: radius.md,
                background: 'rgba(255,255,255,0.04)',
                border: `1px solid ${trimmed ? `${brand.green}66` : `${brand.blue}33`}`,
                color: text.primary,
                fontSize: fontSize.subtitle,
                letterSpacing: '0.08em',
              }}
            />
          ) : (
            <div
              className="flex items-center justify-between"
              style={{
                minHeight: 48,
                padding: `0 ${space.lg}px`,
                borderRadius: radius.md,
                background: `${brand.green}0F`,
                border: `1px solid ${brand.green}4D`,
              }}
            >
              <span
                className="flex items-center"
                style={{ gap: space.sm, color: text.primary, fontSize: fontSize.subtitle, letterSpacing: '0.08em' }}
              >
                <span className="material-icons" style={{ color: brand.green, fontSize: 18 }}>
                  check_circle
                </span>
                {code}
              </span>
              <button
                onClick={() => setEditing(true)}
                style={{ color: brand.blueLight, fontSize: fontSize.caption }}
              >
                {t('inviteConfirm.edit')}
              </button>
            </div>
          )}

          <div
            className="flex items-start"
            style={{
              gap: space.sm,
              padding: space.md,
              borderRadius: radius.md,
              marginTop: space.md,
              marginBottom: space.xl,
              background: blocked ? `${brand.red}0F` : `${brand.blue}0F`,
              border: `1px solid ${blocked ? `${brand.red}33` : `${brand.blue}1F`}`,
            }}
          >
            <span
              className="material-icons flex-shrink-0"
              style={{ color: blocked ? brand.red : brand.blueLight, fontSize: 16, marginTop: 2 }}
            >
              {blocked ? 'error_outline' : 'info'}
            </span>
            <p style={{ color: text.secondary, fontSize: fontSize.caption, lineHeight: 1.6 }}>
              {blocked
                ? t('inviteConfirm.requiredTip')
                : asRoot
                  ? t('inviteConfirm.rootTip')
                  : editing
                    ? t('inviteConfirm.bindTip')
                    : t('inviteConfirm.fromLinkTip')}
            </p>
          </div>

          <button
            onClick={() => confirm(trimmed)}
            disabled={blocked}
            className="w-full font-semibold transition-all duration-200 disabled:opacity-40"
            style={{
              height: 48,
              borderRadius: radius.md,
              background: `linear-gradient(135deg, ${brand.blue}, ${brand.blueDeep})`,
              color: text.primary,
              fontSize: fontSize.body,
              letterSpacing: '0.02em',
            }}
          >
            {t('inviteConfirm.confirm')}
          </button>

          <button
            onClick={cancel}
            className="w-full"
            style={{
              height: 40,
              marginTop: space.sm,
              color: text.muted,
              fontSize: fontSize.body,
            }}
          >
            {t('common.cancel')}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}
